import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';

interface ComplianceMetricsProps {
  framework: string | null;
  days: number;
  dashboardData: any;
}

const ComplianceMetrics: React.FC<ComplianceMetricsProps> = ({ 
  framework, 
  days, 
  dashboardData 
}) => {
  if (!dashboardData) {
    return null;
  }

  // Prepare data for charts
  const getScoreHistoryData = () => {
    if (framework && framework !== 'all') {
      const fw = dashboardData.frameworks.find((f: any) => f.name === framework);
      return fw?.score_history || [];
    }

    // Aggregate all frameworks
    const allHistory: any = {};
    dashboardData.frameworks.forEach((fw: any) => {
      fw.score_history.forEach((entry: any) => {
        if (!allHistory[entry.date]) {
          allHistory[entry.date] = {
            date: entry.date,
            scores: [],
            phi_detections: 0,
            phi_blocked: 0,
          };
        }
        allHistory[entry.date].scores.push(entry.score);
        allHistory[entry.date].phi_detections += entry.phi_detections;
        allHistory[entry.date].phi_blocked += entry.phi_blocked;
      });
    });

    return Object.values(allHistory).map((entry: any) => ({
      date: entry.date,
      score: Math.round(entry.scores.reduce((a: number, b: number) => a + b, 0) / entry.scores.length),
      phi_detections: entry.phi_detections,
      phi_blocked: entry.phi_blocked,
    }));
  };

  const scoreHistory = getScoreHistoryData();

  // Prepare PHI detection trend data
  const phiTrendData = scoreHistory.map((entry: any) => ({
    date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    detections: entry.phi_detections || 0,
    blocked: entry.phi_blocked || 0,
    rate: entry.phi_detections > 0 
      ? Math.round((entry.phi_blocked / entry.phi_detections) * 100) 
      : 100,
  }));

  // Prepare compliance score trend data
  const scoreTrendData = scoreHistory.map((entry: any) => ({
    date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: entry.score || 0,
  }));

  // Prepare framework comparison data (if showing all frameworks)
  const frameworkComparisonData = framework === null || framework === 'all'
    ? dashboardData.frameworks.map((fw: any) => ({
        framework: fw.name,
        score: fw.compliance_score,
        phi_detections: fw.metrics.phi_detections,
        phi_blocked: fw.metrics.phi_blocked,
        breach_attempts: fw.metrics.breach_attempts_blocked,
      }))
    : [];

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Compliance Score Trend */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Compliance Score Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={scoreTrendData}>
                  <defs>
                    <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#1976d2" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#1976d2" 
                    fillOpacity={1} 
                    fill="url(#colorScore)" 
                    name="Compliance Score"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* PHI Detection Trend */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                PHI Detection & Block Rate
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={phiTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="detections" 
                    stroke="#ff9800" 
                    name="PHI Detected"
                    strokeWidth={2}
                  />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="blocked" 
                    stroke="#4caf50" 
                    name="PHI Blocked"
                    strokeWidth={2}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="rate" 
                    stroke="#2196f3" 
                    name="Block Rate %"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Framework Comparison (only when showing all frameworks) */}
        {frameworkComparisonData.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Framework Comparison
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={frameworkComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="framework" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="score" fill="#1976d2" name="Compliance Score" />
                    <Bar dataKey="phi_detections" fill="#ff9800" name="PHI Detections" />
                    <Bar dataKey="phi_blocked" fill="#4caf50" name="PHI Blocked" />
                    <Bar dataKey="breach_attempts" fill="#f44336" name="Breach Attempts Blocked" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Breach Prevention Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Breach Prevention
              </Typography>
              <Box mt={2}>
                {framework === null || framework === 'all' ? (
                  <Grid container spacing={2}>
                    {dashboardData.frameworks.map((fw: any) => (
                      <Grid item xs={12} key={fw.name}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {fw.name}
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {fw.metrics.breach_attempts_blocked}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            breach attempts blocked
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography variant="h2" color="success.main">
                      {dashboardData.frameworks.find((f: any) => f.name === framework)?.metrics.breach_attempts_blocked || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      breach attempts blocked
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Response Time Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Average Response Time
              </Typography>
              <Box mt={2}>
                {framework === null || framework === 'all' ? (
                  <Grid container spacing={2}>
                    {dashboardData.frameworks.map((fw: any) => (
                      <Grid item xs={12} key={fw.name}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {fw.name}
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {fw.metrics.average_response_time_ms}ms
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            average response time
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography variant="h2" color="primary">
                      {dashboardData.frameworks.find((f: any) => f.name === framework)?.metrics.average_response_time_ms || 0}ms
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      average response time
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceMetrics;
